import type { ScheduleTenant } from './leaseScheduleEngine';

/**
 * ⭐⭐ RE-BASING A MARKETED NOI TO IN-PLACE - THE UNDERWRITER'S STANDARD.
 *
 * Debt is sized against income the property is contractually receiving ON THE DAY THE LOAN
 * CLOSES. Contractual step-ups are credit support - they improve coverage across the term - but
 * they do not size the loan, which is why debt yield (in-place NOI / loan) is deliberately
 * forward-blind. Re-basing a marketed figure is the first thing an analyst does, not a courtesy.
 *
 * 🚨 THE DEAL THAT FOUND IT. The Bank of the Sierra OM prints:
 *     PRICE $2,506,802 · CAP RATE 6.00% · NOI $150,408 ¹        ¹ (as of 1/1/2027)
 * The 2.25% escalation lands on the 12/21 lease anniversary, days before 1/1/2027, so $150,408 is
 * the rent AFTER that step and the marketed 6.00% is a FORWARD cap. In place today it is $147,098
 * - a 5.87% cap, 0.13pp lower.
 *
 * ⭐⭐ F1 COULD NOT CATCH THIS. The extractor recorded `monthly_base_rent` as the same forward
 * figure, so the rent roll and the headline agreed and there was nothing to disagree about. The
 * inconsistency was INSIDE the extraction - an NOI explicitly dated 1/1/2027 beside a base rent
 * labelled current - and only the footnote reveals it.
 *
 * ⭐ Joseph's rulebook already says this on the EXPENSE side: "OMs carry the seller's old tax
 * basis", so California taxes are reassessed to the purchase price. This is the same principle on
 * the income side, and the app was applying it to only one of the two.
 *
 * ⛔ IT REFUSES MORE OFTEN THAN IT ACTS. No stated date, a date already past, more than one lease,
 * or no recurring rate to walk back - each returns the figure untouched and says why. Guessing at
 * a re-base would be worse than the defect it fixes.
 */

export interface RebaseInput {
  /** The OM's headline NOI. */
  statedNoi: number;
  /** The date the OM says that figure describes, or null when it does not say. */
  noiAsOf: string | null | undefined;
  /** Today - the day the in-place figure must describe. */
  asOf: string;
  tenants: ScheduleTenant[];
}

export interface RebaseResult {
  /** What to underwrite: the figure walked back to `asOf`. */
  inPlaceNoi: number;
  statedNoi: number;
  statedAsOf: string | null;
  /** How many contractual escalations were removed. 0 means nothing was changed. */
  stepsRemoved: number;
  /** Shown on screen whenever there is something a reader must know. */
  message: string | null;
}

const parse = (s?: string | null): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
  return Number.isNaN(d.getTime()) ? null : d;
};
const money = (v: number) => '$' + Math.round(v).toLocaleString('en-US');

export const rebaseToInPlace = (input: RebaseInput): RebaseResult => {
  const { statedNoi, asOf, tenants } = input;
  const base: RebaseResult = {
    inPlaceNoi: statedNoi, statedNoi, statedAsOf: input.noiAsOf ?? null,
    stepsRemoved: 0, message: null,
  };

  const stated = parse(input.noiAsOf);
  const today = parse(asOf);
  if (!stated || !today) return base;

  if (stated.getTime() <= today.getTime()) {
    // ⛔ A NOI dated in the PAST is a different problem (backlog A1e), and walking it UP would be
    // the flattering direction. Too low is visible; too high destroys data silently.
    return {
      ...base,
      message: `The stated NOI describes ${input.noiAsOf}, which is on or before today - it may be stale. It has been left as stated rather than adjusted upward. Confirm the rent currently in place.`,
    };
  }

  if (tenants.length !== 1) {
    // ⛔ One number covering several leases that escalate on different dates cannot be honestly
    // divided, so it is flagged for a human and left alone.
    return {
      ...base,
      message: `The stated NOI describes ${input.noiAsOf}, a future date, but this property has more than one lease - the figure cannot be attributed between them, so it has NOT been adjusted. Underwrite the rent in place at closing.`,
    };
  }

  const t = tenants[0];
  const rule = (t.escalations ?? []).find(e => e && e.recurrence === 'annual' && e.escalation_type === 'percent');
  const rate = rule ? Number(rule.escalation_value) : NaN;
  const anniversary = parse(t.lease_start);
  if (!rule || !isFinite(rate) || rate <= 0 || !anniversary) {
    return {
      ...base,
      message: `The stated NOI describes ${input.noiAsOf}, a future date, but this lease carries no recurring percentage escalation to walk it back, so it has NOT been adjusted. Underwrite the rent in place at closing.`,
    };
  }

  // Anniversaries strictly after today and on or before the stated date - each one is a step the
  // stated figure contains and today's rent does not.
  let steps = 0;
  for (let y = today.getUTCFullYear() - 1; y <= stated.getUTCFullYear() + 1; y++) {
    const on = new Date(Date.UTC(y, anniversary.getUTCMonth(), anniversary.getUTCDate(), 12));
    if (on.getTime() > today.getTime() && on.getTime() <= stated.getTime()) steps += 1;
  }
  if (steps === 0) return base;

  const inPlaceNoi = statedNoi / Math.pow(1 + rate / 100, steps);
  return {
    inPlaceNoi,
    statedNoi,
    statedAsOf: input.noiAsOf ?? null,
    stepsRemoved: steps,
    message:
      `The OM states this NOI as of ${input.noiAsOf}, which is ${steps} contractual ` +
      `${steps === 1 ? 'escalation' : 'escalations'} ahead of today. The marketed ${money(statedNoi)} is a ` +
      `FORWARD figure; in place today the rent is ${money(inPlaceNoi)}. A lender underwrites the ` +
      `in-place number, so that is what the going-in cap is struck on - the marketed cap is shown beside it.`,
  };
};
